import React, { JSX, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  addBookingSettings,
  updateOnboardingStage,
  CancellationPolicyTemplate,
} from 'features/placeSlice';
import BookingService from 'service/BookingService';
import { CancellationPolicyPicker } from 'components/CancellationPolicyPicker/CancellationPolicyPicker';
import { getCatalog as getCancellationCatalog } from 'service/CancellationPolicyService';
import { notification } from 'antd';
import styled from 'styled-components';
import { FiCheck } from 'react-icons/fi';
import { LuSparkles } from 'react-icons/lu';
import {
  OnboardingShell,
  Field,
  Group,
  Grid,
  TextInput,
  SwitchRow,
  MultiChips,
  TimePicker,
  AdornedInput,
  ErrorText,
  ob,
} from 'components/Onboarding';
import { useOnboardingBackConfirm } from 'library/hooks/useOnboardingBackConfirm';
import {
  getApiResponseMessage,
  getUserFriendlyError,
} from 'library/helpers/errorMessage';
import { transformBookingSettingsFromApi } from 'library/helpers/bookingSettingsFromApi';
import { getEditReturnPath } from 'library/helpers/importEditFlow';
import PropertyService from 'service/PropertyService';
import { CustomPolicyEditor } from 'components/CancellationPolicyPicker/CustomPolicyEditor';
import {
  CUSTOM_POLICY_CODE,
  CustomPolicyDraft,
  draftToPolicy,
  emptyDraft,
  policyToDraft,
  validateDraft,
} from 'library/helpers/customCancellationPolicy';

/** Lowest share of the total a guest can be asked to pay upfront on a part payment. */
const MIN_PARTIAL_PERCENT = 50;

const PAYMENT_OPTIONS_TOOLTIP =
  'Choose how guests pay to book: the full amount upfront, or let them choose between paying in full and reserving with a part payment (at least 50%) and settling the rest at check-in.';

interface BookingDetailsState {
  checkInTime: string;
  checkOutTime: string;
  bookingApproval: string;
  restrictions: string[];
  partialPaymentAllowed: boolean;
  partialPaymentPercentage: number;
}

interface ErrorState {
  checkInTime: string;
  checkOutTime: string;
  bookingApproval: string;
  partialPaymentPercentage: string;
  cancellationPolicy: string;
}

interface RestrictionItem {
  label: string;
}

function BookingSettings(): JSX.Element {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const useQuery = (): URLSearchParams => {
    return new URLSearchParams(useLocation().search);
  };
  let query = useQuery();
  const hostPropertyId = query.get('id');

  const [error, setError] = useState<ErrorState>({
    checkInTime: '',
    checkOutTime: '',
    bookingApproval: '',
    partialPaymentPercentage: '',
    cancellationPolicy: '',
  });

  const { BackConfirmModal } = useOnboardingBackConfirm();

  const [bookingDetails, setBookingDetails] = useState<BookingDetailsState>({
    checkInTime: '',
    checkOutTime: '',
    bookingApproval: 'instantBooking',
    restrictions: [],
    partialPaymentAllowed: false,
    partialPaymentPercentage: 50,
  });
  const [cancellationPolicyCode, setCancellationPolicyCode] = useState<
    string | null
  >(null);
  const [customDraft, setCustomDraft] = useState<CustomPolicyDraft>(emptyDraft());
  const [catalog, setCatalog] = useState<CancellationPolicyTemplate[]>([]);
  const [partialPaymentInput, setPartialPaymentInput] = useState<string>('50');
  const restrictions: RestrictionItem[] = [
    { label: 'No Alcohol' },
    { label: 'No Smoking' },
    { label: 'No Firecrackers' },
    { label: 'No Illegal Activities' },
    { label: 'No Events' },
    { label: 'No Parties' },
  ];
  const { bookingSettings, propertyId, onboardingStage } = useSelector(
    (state: any) => ({
      bookingSettings: state.bookingSettings,
      propertyId: state.propertyId,
      onboardingStage: state.onboardingStage,
    }),
  );
  // the advance as it is stored, to tell an untouched old value from one the host just typed
  const storedPartialPercentage = Math.round(Number(bookingSettings?.partialPaymentPercentage));

  const resolvePropertyId = (): string | number | null => {
    return (
      hostPropertyId ||
      propertyId ||
      localStorage.getItem('id') ||
      localStorage.getItem('newPropertId')
    );
  };

  const scrollToTop = (): void => {
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    getCancellationCatalog()
      .then(setCatalog)
      .catch(() => setCatalog([]));
  }, []);

  useEffect(() => {
    setBookingDetails({
      ...bookingSettings,
      partialPaymentAllowed:
        typeof bookingSettings?.partialPaymentAllowed === 'boolean'
          ? bookingSettings.partialPaymentAllowed
          : false,
      partialPaymentPercentage: (() => {
        const n = Number(bookingSettings?.partialPaymentPercentage);
        // keep a stored advance below the minimum as it is; the minimum applies to edits
        if (!Number.isFinite(n) || n < 1) return 50;
        return Math.min(100, Math.round(n));
      })(),
    });
  }, [bookingSettings]);

  useEffect(() => {
    setPartialPaymentInput(String(bookingDetails.partialPaymentPercentage));
  }, [bookingDetails.partialPaymentPercentage]);

  useEffect(() => {
    const code = bookingSettings?.cancellationPolicyCode;
    setCancellationPolicyCode(typeof code === 'string' ? code : null);
    if (code === CUSTOM_POLICY_CODE && bookingSettings?.customCancellationPolicy) {
      setCustomDraft(policyToDraft(bookingSettings.customCancellationPolicy));
    }
  }, [bookingSettings]);

  useEffect(() => {
    const shouldLoadPersistedBooking =
      Boolean(hostPropertyId) ||
      (typeof onboardingStage === 'number' && onboardingStage >= 4);
    if (!shouldLoadPersistedBooking) return;

    const id =
      hostPropertyId ||
      propertyId ||
      localStorage.getItem('id') ||
      localStorage.getItem('newPropertId');
    if (!id) return;
    let cancelled = false;
    PropertyService.getBookingSettingsByPropertyId(id)
      .then((responseData) => {
        if (cancelled) return;
        const { responseInformation, result } = responseData;
        if ((responseInformation as any)?.responseCode === 0 && result) {
          const dataSet = transformBookingSettingsFromApi(result);
          if (dataSet) dispatch(addBookingSettings(dataSet as any));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [hostPropertyId, propertyId, onboardingStage, dispatch]);

  const handleBookingChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setError((prev) => ({
      ...prev,
      bookingApproval: '',
    }));

    setBookingDetails((prev) => ({
      ...prev,
      bookingApproval: e.target.value,
    }));
  };

  const handleTimeChange = (value: string, key: string): void => {
    if (value) {
      setError((prev) => ({ ...prev, [key]: '' }));
    }
    setBookingDetails((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const validateEntries = (): boolean => {
    const newErrors: ErrorState = {
      checkInTime: '',
      checkOutTime: '',
      bookingApproval: '',
      partialPaymentPercentage: '',
      cancellationPolicy: '',
    };

    if (!bookingDetails.checkInTime) {
      newErrors.checkInTime = 'Please set checkin time';
    }
    if (!bookingDetails.checkOutTime) {
      newErrors.checkOutTime = 'Please set checkout time';
    }

    if (bookingDetails.partialPaymentAllowed) {
      const p = Number(partialPaymentInput);
      // An advance saved before the minimum existed may stay as it is; a changed value must meet it.
      const unchanged = p === bookingDetails.partialPaymentPercentage && p === storedPartialPercentage;
      if (!Number.isFinite(p) || p > 100 || p < (unchanged ? 1 : MIN_PARTIAL_PERCENT)) {
        newErrors.partialPaymentPercentage = `Enter a whole number between ${MIN_PARTIAL_PERCENT} and 100.`;
      }
    }

    newErrors.cancellationPolicy = !cancellationPolicyCode
      ? 'Please select a cancellation policy.'
      : cancellationPolicyCode === CUSTOM_POLICY_CODE && validateDraft(customDraft)
      ? 'Complete your custom cancellation policy.'
      : '';

    setError(newErrors);
    return Object.values(newErrors).some((v) => v !== '');
  };

  const nextHandler = async (): Promise<void> => {
    if (validateEntries()) {
      notification.error({
        message: 'Please complete required fields',
        description:
          `Set check-in and check-out times, and a valid part-payment percentage (${MIN_PARTIAL_PERCENT}–100) when partial payment is chosen.`,
      });
      scrollToTop();
      return;
    }

    const resolvedPartialPct = bookingDetails.partialPaymentAllowed
      ? Math.min(100, Math.round(Number(partialPaymentInput)))
      : bookingDetails.partialPaymentPercentage;

    const bookingPayload: BookingDetailsState = {
      ...bookingDetails,
      partialPaymentPercentage: resolvedPartialPct,
    };

    const customPolicy =
      cancellationPolicyCode === CUSTOM_POLICY_CODE ? draftToPolicy(customDraft) : null;
    dispatch(
      addBookingSettings({
        ...bookingPayload,
        cancellationPolicyCode,
        customCancellationPolicy: customPolicy,
      }),
    );
    try {
      const effectivePropertyId = resolvePropertyId();
      if (!effectivePropertyId) {
        notification.error({
          message: 'Missing property id',
          description:
            'Could not determine property id to save booking settings. Please reload and try again.',
        });
        return;
      }
      const finalBookingSetting = {
        propertyId: effectivePropertyId,
        checkInTime: bookingPayload.checkInTime,
        checkOutTime: bookingPayload.checkOutTime,
        cancellationPolicyCode: cancellationPolicyCode,
        ...(customPolicy ? { customCancellationPolicy: customPolicy } : {}),
        propertyPolicy: JSON.stringify(bookingPayload.restrictions),
        partialPaymentAllowed: bookingPayload.partialPaymentAllowed,
        partialPaymentPercentage: bookingPayload.partialPaymentPercentage,
        onboardingStage: 4,
        approvalRequiredForBooking:
          false,
      };
      const res =
        await BookingService.savePropertyBookingSettings(finalBookingSetting);

      if (
        res.responseInformation &&
        res.responseInformation?.responseCode === 0
      ) {
        // The button reads "Update" instead of "Next" whenever hostPropertyId is already set
        // (an existing property) — that's an edit, so it shouldn't get the onboarding-completion
        // copy meant for a host still walking through creating a brand new property.
        if (hostPropertyId) {
          notification.success({
            message: 'Booking settings updated',
            description: 'Your changes have been saved.',
          });
        }
        dispatch(updateOnboardingStage(4));
        // House rules are the next step; it sends the host on to the review.
        // `edit=1` marks an existing listing being edited, which skips the hosting-plan step.
        // Started from a Review/Verify page: this page has saved everything itself, so go straight
        // back there instead of on to House Rules.
        const returnPath = getEditReturnPath(effectivePropertyId);
        if (returnPath) {
          navigate(returnPath);
          return;
        }
        navigate(
          `/host/house-rules?id=${effectivePropertyId}${hostPropertyId ? '&edit=1' : ''}`,
        );
      } else {
        notification.error({
          message: 'Unable to save booking settings',
          description: getApiResponseMessage(
            res,
            'Could not save booking settings. Please try again.',
          ),
        });
        return;
      }
    } catch (error: any) {
      notification.error({
        message: 'Unable to save booking settings',
        description: getUserFriendlyError(
          error,
          'Could not save booking settings. Please try again.',
        ),
      });
      return;
    }
  };

  // const backHandler = () => {
  //   dispatch(addBookingSettings(bookingDetails));
  //   navigate('/host/property-listing');
  // };

  const addItemsHandle = (restriction: string): void => {
    if (!bookingDetails.restrictions.includes(restriction))
      setBookingDetails((prev) => ({
        ...prev,
        restrictions: [...prev.restrictions, restriction],
      }));
    else {
      // const updatedRestriction = bookingDetails.restrictions.filter(
      //   (item) => item !== restriction,
      // );
      setBookingDetails((prev) => ({
        ...prev,
        restrictions: [
          ...prev.restrictions.filter((item) => item !== restriction),
        ],
      }));
    }
  };

  /* ---- live "still needed" list ---- */
  const partialPctNumber = Number(partialPaymentInput);
  const pendingItems = [
    !bookingDetails.checkInTime && 'Set the check-in time.',
    !bookingDetails.checkOutTime && 'Set the check-out time.',
    bookingDetails.partialPaymentAllowed &&
      (!Number.isFinite(partialPctNumber) ||
        partialPctNumber < MIN_PARTIAL_PERCENT ||
        partialPctNumber > 100) &&
      `Enter a whole number between ${MIN_PARTIAL_PERCENT} and 100 for the part payment.`,
    !cancellationPolicyCode && 'Choose a cancellation policy.',
    cancellationPolicyCode === CUSTOM_POLICY_CODE &&
      validateDraft(customDraft) !== null &&
      'Complete your custom cancellation policy.',
  ].filter((item): item is string => typeof item === 'string' && item !== '');

  const paymentFull = !bookingDetails.partialPaymentAllowed;
  const setPaymentMode = (partial: boolean): void => {
    setError((prev) => ({ ...prev, partialPaymentPercentage: '' }));
    setBookingDetails((prev) => ({
      ...prev,
      partialPaymentAllowed: partial,
      partialPaymentPercentage: partial
        ? prev.partialPaymentPercentage >= 1 && prev.partialPaymentPercentage <= 100
          ? prev.partialPaymentPercentage
          : 50
        : prev.partialPaymentPercentage,
    }));
  };

  return (
    <OnboardingShell
      section={6}
      title="Booking, payment & cancellation"
      onContinue={nextHandler}
      continueLabel={hostPropertyId ? 'Update' : 'Next'}
      continueDisabled={pendingItems.length > 0}
      pending={pendingItems}
      modal={BackConfirmModal}
    >
      <Group bare>
        <Grid $cols={2} $gap={20}>
          <Field label="Set check-in time" error={error.checkInTime}>
            <TimePicker
              ariaLabel="Check-in time"
              value={bookingDetails.checkInTime}
              onChange={(value) => handleTimeChange(value, 'checkInTime')}
            />
          </Field>
          <Field label="Set check-out time" error={error.checkOutTime}>
            <TimePicker
              ariaLabel="Check-out time"
              value={bookingDetails.checkOutTime}
              onChange={(value) => handleTimeChange(value, 'checkOutTime')}
            />
          </Field>
        </Grid>
      </Group>

      <Group title="What guests pay to book" info={PAYMENT_OPTIONS_TOOLTIP}>
        <PayCards>
          {/* The host picks one: full payment only, or a part payment (guests then see both). */}
          <PayCard
            type="button"
            $active={paymentFull}
            aria-pressed={paymentFull}
            onClick={() => setPaymentMode(false)}
          >
            <span className="t">
              Pay in full {paymentFull ? <FiCheck size={16} /> : null}
            </span>
            <span className="s">Guests pay the whole amount when they book.</span>
          </PayCard>
          <PayCard
            type="button"
            $active={!paymentFull}
            aria-pressed={!paymentFull}
            onClick={() => setPaymentMode(true)}
          >
            <span className="flag">
              <LuSparkles size={12} />
              Guests look for flexible payments
            </span>
            <span className="t">
              Partial payment {!paymentFull ? <FiCheck size={16} /> : null}
            </span>
            <span className="s">
              Guests pay a percentage now and the rest at check-in. They can still choose to pay in full.
            </span>
          </PayCard>
        </PayCards>

        {!paymentFull ? (
          <>
            <Field
              label="Percentage payable at booking"
              hint={`Minimum ${MIN_PARTIAL_PERCENT}%`}
              error={error.partialPaymentPercentage}
            >
              <AdornedInput>
                <TextInput
                  type="number"
                  inputMode="numeric"
                  min={MIN_PARTIAL_PERCENT}
                  max={100}
                  aria-label="Partial payment percentage"
                  style={{ paddingRight: 36 }}
                  $invalid={!!error.partialPaymentPercentage}
                  value={partialPaymentInput}
                  onChange={(e) => {
                    setPartialPaymentInput(e.target.value);
                    setError((prev) => ({
                      ...prev,
                      partialPaymentPercentage: '',
                    }));
                  }}
                  onBlur={() => {
                    const n = Number(partialPaymentInput);
                    if (!Number.isFinite(n) || n < MIN_PARTIAL_PERCENT) {
                      setPartialPaymentInput('50');
                      setBookingDetails((prev) => ({
                        ...prev,
                        partialPaymentPercentage: 50,
                      }));
                      return;
                    }
                    const clamped = Math.min(100, Math.max(MIN_PARTIAL_PERCENT, Math.round(n)));
                    setPartialPaymentInput(String(clamped));
                    setBookingDetails((prev) => ({
                      ...prev,
                      partialPaymentPercentage: clamped,
                    }));
                  }}
                />
                <span className="post">%</span>
              </AdornedInput>
            </Field>
            <Note>
              Guests will see both options: pay in full, or pay {partialPaymentInput || MIN_PARTIAL_PERCENT}% now.
              The balance amount needs to be collected by you at check-in via Unpaqd.
            </Note>
          </>
        ) : null}
      </Group>

      <Group
        title="Cancellation policy"
        note="Choose the cancellation policy guests see for this property."
      >
        <CancellationPolicyPicker
          value={cancellationPolicyCode}
          catalog={catalog}
          onChange={setCancellationPolicyCode}
          customSelected={cancellationPolicyCode === CUSTOM_POLICY_CODE}
          onSelectCustom={() => setCancellationPolicyCode(CUSTOM_POLICY_CODE)}
          customContent={<CustomPolicyEditor draft={customDraft} onChange={setCustomDraft} />}
        />
        {error.cancellationPolicy ? (
          <ErrorText role="alert">{error.cancellationPolicy}</ErrorText>
        ) : null}
      </Group>

    </OnboardingShell>
  );
}

const Note = styled.p`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${ob.muted};
`;

const PayCards = styled.div`
  display: grid;
  gap: 12px;
  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const PayCard = styled.button<{ $active?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  border: 1px solid ${({ $active }) => ($active ? ob.primary : ob.border)};
  border-radius: 12px;
  background: ${({ $active }) => ($active ? 'rgba(255, 240, 230, 0.4)' : ob.card)};
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: all 140ms ease;
  &:hover {
    border-color: ${({ $active }) => ($active ? ob.primary : 'rgba(255,113,15,0.3)')};
  }
  .t {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: ${ob.text};
    svg {
      color: ${ob.primary};
    }
  }
  .s {
    font-size: 13px;
    line-height: 1.5;
    color: ${ob.muted};
  }
  .flag {
    position: absolute;
    right: 16px;
    bottom: -10px;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 999px;
    background: ${ob.primary};
    box-shadow: 0 4px 10px -4px rgba(255, 113, 15, 0.6);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.02em;
    pointer-events: none;
  }
`;

export default BookingSettings;
